import { useContext, useMemo, useRef, useState } from 'react'
import { Bell, CalendarClock, CalendarDays, House, Inbox, NotebookPen, Plus, Search, Sun } from 'lucide-react'
import { useRouterState } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { AiSparkleButton } from '@/components/app/ai/AiSparkle'
import { useTodayTasks } from '@/routes/features/tasks/hooks/useTodayTasks'
import { useInboxTasks } from '@/routes/features/tasks/hooks/useInboxTasks'
import { useUpcomingTasks } from '@/routes/features/tasks/hooks/useUpcomingTasks'
import { useSomedayTasks } from '@/routes/features/tasks/hooks/useSomedayTasks'
import { NavMain } from '@/components/app/layout/NavMain'
import { SidebarGroup, useSidebar  } from '@/components/ui/sidebar'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import useLoginStore from '@/store/LoginStore'
import { useTimezone } from '@/store/UserPreferencesStore'
import { ProjectSidebar } from '@/components/app/sidebars/project'
import { TagsSidebarSection } from '@/components/app/sidebars/task/TagsSidebarSection'
import { useUnreadCount } from '@/routes/features/notifications/hooks'
import { QuickCreateTaskDialog } from '@/components/app/QuickCreateTaskDialog'
import { AppLayoutContext } from '@/context/AppLayoutContext'
import { useProjects } from '@/routes/features/tasks/hooks/useProjects'
import { useAiAvailable } from '@/lib/ai/useAiAvailable'
import { SpaceDrillIn } from '@/components/app/sidebars/spaces/SpaceDrillIn'
import { useSpaceDrill } from '@/components/app/sidebars/spaces/useSpaceDrill'
import {
  getTodayDateString,
} from '@/routes/features/notes/utils/noteDateUtils'

/**
 * Normalizes URL paths by removing trailing slashes except for root path.
 *
 * @param path - The URL path to normalize
 * @returns Normalized path string
 */
const normalizePath = (path: string) => {
  if (!path) {
    return path
  }

  if (path === '/') {
    return path
  }

  return path.replace(/\/+$/, '')
}

/**
 * Task management sidebar component that provides navigation for task views.
 *
 * Features:
 * - Smart task folders (Inbox, Today, Upcoming, Someday) with live counts
 * - Active state highlighting based on current route
 * - Integrated area and project navigation
 * - Real-time task count updates from multiple data sources
 *
 * The sidebar dynamically fetches task counts from different filtered views
 * and provides a unified navigation experience for task management.
 *
 * @example
 * ```tsx
 * // Used within app layout
 * <AppSidebar>
 *   <TaskSidebar />
 * </AppSidebar>
 * ```
 */
export function TaskSidebar() {
  const { t } = useTranslation('navigation')
  const workspaceSlug = useLoginStore((state) => state.workspaceSlug)
  const workspaceId = useLoginStore((state) => state.workspaceId)
  const timezone = useTimezone()
  const { state: sidebarState } = useSidebar()
  const isExpanded = sidebarState === 'expanded'
  const layoutContext = useContext(AppLayoutContext)
  const [isQuickCreateOpen, setIsQuickCreateOpen] = useState(false)
  const aiAvailable = useAiAvailable()
  const { count: unreadCount } = useUnreadCount()
  const { projects } = useProjects()
  const { drilledSpace, routeSpaceId, drillInto } = useSpaceDrill()
  // Drilling in slides the space in from the right; "All spaces" slides the
  // top level back in from the left (design 21 C).
  const lastDrilledRef = useRef<string | null>(drilledSpace?.id ?? null)
  const showDrill = isExpanded && drilledSpace !== null
  const panelKey = showDrill ? `space:${drilledSpace.id}` : 'top'
  const slideFrom = showDrill ? 'right' : lastDrilledRef.current ? 'left' : null
  lastDrilledRef.current = showDrill ? drilledSpace.id : null

  // Get task counts from each hook
  // Note: Each hook internally calls useStatuses, but React handles duplicate hook calls
  const { tasks: inboxTasks } = useInboxTasks()
  const { tasks: todayTasks } = useTodayTasks()
  const { tasks: upcomingTasks } = useUpcomingTasks()
  const { tasks: somedayTasks } = useSomedayTasks()

  const inboxCount = inboxTasks?.length ?? 0
  const todayCount = todayTasks?.length ?? 0
  const upcomingCount = upcomingTasks?.length ?? 0
  const somedayCount = somedayTasks?.length ?? 0

  const currentPathname = useRouterState({
    select: (state) => state.location.pathname,
  })
  const normalizedCurrentPath = normalizePath(currentPathname)

  const routeProjectKey = useMemo(() => {
    const projectRouteMatch = currentPathname.match(
      /^\/[^/]+\/projects\/([^/]+)(?:\/.*)?$/i,
    )

    if (projectRouteMatch) {
      return projectRouteMatch[1]
    }

    const spaceProjectRouteMatch = currentPathname.match(
      /^\/[^/]+\/spaces\/[^/]+\/projects\/([^/]+)(?:\/.*)?$/i,
    )

    return spaceProjectRouteMatch?.[1] ?? null
  }, [currentPathname])

  const quickCreateDefaultProjectId = useMemo(() => {
    if (!routeProjectKey) {
      return null
    }

    const normalizedProjectKey = routeProjectKey.toLowerCase()

    return (
      projects.find(
        (project) => project.projectKey.toLowerCase() === normalizedProjectKey,
      )?.id ?? null
    )
  }, [projects, routeProjectKey])

  const navMainItems = useMemo(
    () => [
      {
        title: t('home'),
        url: `/${workspaceSlug}`,
        icon: House,
        isActive: normalizePath(`/${workspaceSlug}`) === normalizedCurrentPath,
      },
      {
        title: t('inbox'),
        url: `/${workspaceSlug}/tasks`,
        icon: Inbox,
        isActive:
          normalizePath(`/${workspaceSlug}/tasks`) === normalizedCurrentPath,
        count: inboxCount,
        countTone: 'brand' as const,
      },
      {
        title: t('today'),
        url: `/${workspaceSlug}/tasks/today`,
        icon: Sun,
        isActive:
          normalizePath(`/${workspaceSlug}/tasks/today`) ===
          normalizedCurrentPath,
        count: todayCount,
      },
      {
        title: t('next7Days'),
        url: `/${workspaceSlug}/tasks/upcoming`,
        icon: CalendarDays,
        isActive:
          normalizePath(`/${workspaceSlug}/tasks/upcoming`) ===
          normalizedCurrentPath,
        count: upcomingCount,
      },
      {
        title: t('someday'),
        url: `/${workspaceSlug}/tasks/someday`,
        icon: CalendarClock,
        isActive:
          normalizePath(`/${workspaceSlug}/tasks/someday`) ===
          normalizedCurrentPath,
        count: somedayCount,
      },
    ],
    [
      inboxCount,
      normalizedCurrentPath,
      somedayCount,
      todayCount,
      upcomingCount,
      workspaceSlug,
      t,
    ],
  )

  const quickCreate = (
    <QuickCreateTaskDialog
      open={isQuickCreateOpen}
      onOpenChange={setIsQuickCreateOpen}
      defaultProjectId={quickCreateDefaultProjectId}
    />
  )

  if (showDrill) {
    return (
      <>
        <div key={panelKey} className={slideClass(slideFrom)}>
          <SpaceDrillIn space={drilledSpace} workspaceSlug={workspaceSlug} onBack={() => drillInto(null)} />
        </div>
        {quickCreate}
      </>
    )
  }

  return (
    <div key={panelKey} className={slideClass(slideFrom)}>
      {isExpanded && (
        <SidebarGroup className="px-3 py-2">
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => layoutContext?.openSearch()}
              className="flex h-[30px] min-w-0 flex-1 items-center gap-2 rounded-md bg-secondary px-2.5 text-[13px] text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
            >
              <Search className="h-3.5 w-3.5 shrink-0" />
              <span className="flex-1 truncate text-left">{t('sidebar.searchOrJump')}</span>
              <kbd className="pointer-events-none hidden select-none font-mono text-[10px] font-medium sm:inline">
                &#8984;K
              </kbd>
            </button>
            {aiAvailable && (
              <AiSparkleButton
                size="sm"
                aria-label={t('sidebar.askAi')}
                title={t('sidebar.askAi')}
                className="h-[30px] w-[30px] p-0 shrink-0"
                iconClassName="size-4"
                onClick={() => layoutContext?.openAskWorkspace()}
              />
            )}
            <Button
              variant="ghost"
              size="icon"
              className="size-[30px] shrink-0"
              aria-label={t('newTask')}
              title={t('newTask')}
              onClick={() => setIsQuickCreateOpen(true)}
            >
              <Plus className="h-4 w-4" />
            </Button>
          </div>
        </SidebarGroup>
      )}
      <NavMain items={navMainItems} />
      <NavMain
        items={[
          {
            title: t('notifications'),
            url: `/${workspaceSlug}/notifications`,
            icon: Bell,
            isActive:
              normalizePath(`/${workspaceSlug}/notifications`) ===
              normalizedCurrentPath,
            count: unreadCount,
          },
          {
            title: t('notes'),
            url: `/${workspaceSlug}/notes/daily/${getTodayDateString(timezone)}`,
            icon: NotebookPen,
            isActive: normalizedCurrentPath.startsWith(
              `/${workspaceSlug}/notes`,
            ),
          },
        ]}
      />
      <TagsSidebarSection
        workspaceId={workspaceId}
        workspaceSlug={workspaceSlug}
        currentPath={normalizedCurrentPath}
      />
      <ProjectSidebar
        workspaceId={workspaceId}
        workspaceSlug={workspaceSlug}
        routeSpaceId={routeSpaceId}
        onDrillIn={(spaceId) => drillInto(spaceId)}
      />
      {quickCreate}
    </div>
  )
}

/** ≤200ms slide between the top level and a space; none with reduced motion. */
function slideClass(from: 'left' | 'right' | null) {
  if (!from) return undefined
  return cn(
    'animate-in fade-in-0 duration-200 ease-out motion-reduce:animate-none',
    from === 'right' ? 'slide-in-from-right-6' : 'slide-in-from-left-6',
  )
}

export default TaskSidebar
